import React from "react";
import { Box } from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Offer, CompletionTimeCode, OfferStatusCode } from "../../models/models";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

interface OfferTimelineCardProps {
  offer: Partial<Offer>;
}

const DetailRow: React.FC<{ label: string; value: string | React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: "8px" }}>
    <Box sx={{ fontSize: "13px", fontWeight: 500, fontFamily: dt.font, color: dt.textSecondary, flex: "0 0 45%" }}>
      {label}
    </Box>
    <Box sx={{ fontSize: "13px", fontWeight: 600, fontFamily: dt.fontMono, color: dt.textPrimary, textAlign: "right", flex: "0 0 55%" }}>
      {value}
    </Box>
  </Box>
);

const OfferTimelineCard: React.FC<OfferTimelineCardProps> = ({ offer }) => {
  const { translate } = useTranslation(["serviceOffers"]);

  const getCompletionTimeLabel = (): string => {
    switch (offer.completionTimeCode) {
      case CompletionTimeCode.sameDay: return translate("completionTimeCodes.sameDay");
      case CompletionTimeCode.oneToTwoDays: return translate("completionTimeCodes.oneToTwoDays");
      case CompletionTimeCode.threeToFiveDays: return translate("completionTimeCodes.threeToFiveDays");
      case CompletionTimeCode.oneWeek: return translate("completionTimeCodes.oneWeek");
      case CompletionTimeCode.twoWeeks: return translate("completionTimeCodes.twoWeeks");
      case CompletionTimeCode.custom: return offer.customCompletionTime || "\u2014";
      default: return "\u2014";
    }
  };

  const isConfirmedOrLater =
    offer.statusCode != null &&
    offer.statusCode >= OfferStatusCode.confirmed &&
    offer.confirmedArrivalDate;

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radius,
        padding: "20px",
      }}
    >
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "1px",
          color: dt.textMuted,
          fontFamily: dt.font,
          mb: "12px",
        }}
      >
        {translate("timeline")}
      </Box>

      <DetailRow
        label={translate("earliestStartDate")}
        value={offer.earliestStartDate ? formatUiDate(offer.earliestStartDate) : "\u2014"}
      />
      <DetailRow label={translate("estimatedDuration")} value={offer.estimatedDuration || "\u2014"} />
      <DetailRow label={translate("completionTime")} value={getCompletionTimeLabel()} />

      {/* Confirmed arrival */}
      {isConfirmedOrLater && (
        <Box
          sx={{
            mt: "12px",
            p: "12px 14px",
            backgroundColor: "#E8F8F5",
            borderLeft: "3px solid #1ABC9C",
            borderRadius: dt.radiusSm,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#1ABC9C" }} />
          <Box>
            <Box sx={{ fontSize: "13px", fontWeight: 600, color: "#1ABC9C", fontFamily: dt.font }}>
              {formatUiDate(offer.confirmedArrivalDate, "longWeekdayDate")}
              {offer.confirmedArrivalTimeFrom && offer.confirmedArrivalTimeTo && (
                <Box component="span" sx={{ fontWeight: 500, color: dt.textSecondary, ml: 1, fontFamily: dt.fontMono, fontSize: "12px" }}>
                  {offer.confirmedArrivalTimeFrom} - {offer.confirmedArrivalTimeTo}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OfferTimelineCard;
